// Admin — Invite a new user (Wave E).
//
// Per server `createStaffSchema`: name, email, username, role + optional
// phone/address/postcode/password. If password is omitted the server
// generates one and returns it in the response (we surface it in the
// success toast for the admin to communicate out-of-band).

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Input } from '../../../components/form/Input';
import { Select } from '../../../components/form/Select';
import { Textarea } from '../../../components/form/Textarea';
import { useCreateStaff } from './hooks/useUsers';
import type { CreateStaffInput, StaffRole } from '../../../types/api';
import styles from './UserFormPage.module.css';

const ROLE_OPTIONS = [
  { value: 'staff', label: 'Staff' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
];

export function UserInvitePage(): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateStaff();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [role, setRole] = useState<StaffRole>('staff');
  const [password, setPassword] = useState('');

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const payload: CreateStaffInput = {
      name: name.trim(),
      email: email.trim(),
      username: username.trim(),
      role,
      ...(phone ? { phone: phone.trim() } : {}),
      ...(address ? { address: address.trim() } : {}),
      ...(postcode ? { postcode: postcode.trim() } : {}),
      ...(password ? { password } : {}),
    };
    create.mutate(payload, {
      onSuccess: (data) => {
        navigate(`/admin/users/${encodeURIComponent(data.uuid)}`);
      },
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Invite User"
        subtitle="Add a new staff member to your company"
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/admin/users')}
          >
            Back
          </Button>
        }
      />

      <Card>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.row}>
            <Input
              label="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="off"
            />
            <Input
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              helper="Letters, digits, dot, underscore, dash. Used for login."
              autoComplete="off"
            />
          </div>

          <div className={styles.row}>
            <Input
              label="Email Address"
              variant="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="off"
            />
            <Input
              label="Phone Number"
              variant="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="off"
            />
          </div>

          <div className={styles.row}>
            <Select
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value as StaffRole)}
              options={ROLE_OPTIONS}
              required
            />
            <Input
              label="Postcode"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              autoComplete="off"
            />
          </div>

          <Textarea
            label="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rows={3}
          />

          <Input
            label="Initial Password (optional)"
            variant="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helper="Leave blank to have the server generate one. Min 8 characters."
            autoComplete="new-password"
          />

          <div className={styles.footer}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/admin/users')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={UserPlus}
              loading={create.isPending}
            >
              Send Invite
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
